'use client'

import React, { useEffect, useRef } from 'react'
import Image from 'next/image'
import type { StaticImageData } from 'next/image'
import { Box, Flex, Text, UnstyledButton } from '@mantine/core'
import { useInView } from 'framer-motion'
import { PlayIcon } from './Icons'

type MediaShellProps = {
    backgroundVideoSrc?: string
    mediaImageSrc?: string | StaticImageData
    mediaImageAlt?: string
    mediaOverlayTitle: string
    mediaOverlaySubtitle?: string
    showPlayControl: boolean
    useCardsAsContent?: boolean
    onPlayClick: () => void
}

export function MediaShell({
    backgroundVideoSrc,
    mediaImageSrc,
    mediaImageAlt,
    mediaOverlayTitle,
    mediaOverlaySubtitle,
    showPlayControl,
    useCardsAsContent,
    onPlayClick,
}: MediaShellProps) {
    const backgroundVideoRef = useRef<HTMLVideoElement>(null)
    const mediaShellRef = useRef<HTMLDivElement>(null)
    const inView = useInView(mediaShellRef, { once: true, margin: '-120px 0px' })

    const hasBackgroundVideo = Boolean(backgroundVideoSrc)

    useEffect(() => {
        const video = backgroundVideoRef.current
        if (!video || !hasBackgroundVideo || !inView) return

        const tryPlay = () => {
            video.play().catch(() => {})
        }
        tryPlay()
        video.addEventListener('canplay', tryPlay)
        return () => video.removeEventListener('canplay', tryPlay)
    }, [hasBackgroundVideo, inView])

    return (
        <Box
            ref={mediaShellRef}
            pos="relative"
            w="100%"
            {...(useCardsAsContent
                ? {
                      mih: { base: 220, lg: 0 },
                      h: { base: 260, sm: 320, lg: '100%' },
                  }
                : {
                      mih: { base: 220, sm: 300 },
                  })}
            style={{
                overflow: 'hidden',
                backgroundColor: '#d1ced9',
                minWidth: 0,
                borderRadius: 20,
                flex: '1 1 0',
                ...(useCardsAsContent ? {} : { aspectRatio: '16 / 9' }),
            }}
        >
            {hasBackgroundVideo && inView && (
                <video
                    ref={backgroundVideoRef}
                    src={backgroundVideoSrc}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    aria-hidden={!showPlayControl}
                    style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                    }}
                />
            )}
            {!hasBackgroundVideo && mediaImageSrc && (
                <Image
                    src={mediaImageSrc}
                    alt={mediaImageAlt ?? ''}
                    fill
                    sizes="(max-width: 768px) 100vw, 50vw"
                    style={{ objectFit: 'cover' }}
                />
            )}
            {(hasBackgroundVideo || mediaImageSrc) && (
                <>
                    <Box
                        pos="absolute"
                        inset={0}
                        style={{
                            zIndex: 1,
                            pointerEvents: 'none',
                            background:
                                'linear-gradient(to top, rgba(0, 0, 0, 0.78) 0%, rgba(0, 0, 0, 0.35) 42%, transparent 72%)',
                        }}
                    />
                    <Flex
                        pos="absolute"
                        inset={0}
                        direction="column"
                        justify="flex-end"
                        align="flex-start"
                        p={32}
                        gap={32}
                        style={{ zIndex: 2 }}
                    >
                        {showPlayControl ? (
                            <UnstyledButton
                                onClick={onPlayClick}
                                aria-label="Play video"
                                type="button"
                                w={70}
                                h={70}
                                styles={{
                                    root: {
                                        transition: 'transform 150ms ease, opacity 150ms ease',
                                        '&:hover': {
                                            transform: 'scale(1.06)',
                                            opacity: 0.95,
                                        },
                                        '&:active': {
                                            transform: 'scale(0.98)',
                                        },
                                    },
                                }}
                            >
                                <PlayIcon />
                            </UnstyledButton>
                        ) : null}
                        <Box>
                            <Text fw={400} fz={32} c="white" lh="120%" lts={-0.96} m={0}>
                                {mediaOverlayTitle}
                            </Text>
                            {mediaOverlaySubtitle ? (
                                <Text
                                    fz={20}
                                    fw={450}
                                    lh="130%"
                                    m={0}
                                    mt={24}
                                    style={{ color: 'rgba(255, 255, 255, 0.9)' }}
                                >
                                    {mediaOverlaySubtitle}
                                </Text>
                            ) : null}
                        </Box>
                    </Flex>
                </>
            )}
        </Box>
    )
}
